/* Copyright (c) 2019-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Subscription, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, finalize, mergeMap } from 'rxjs/operators';
import { extend, merge, isUndefined, isNil, isEmpty } from 'lodash';
import { GfcEventsEndpointsService } from 'gfc-events/lib/sdks/gfc-events-endpoints.service';
import { UndefinedString } from 'gfc-events/lib/models/common-data.models';
import { generateUuid } from 'utils/lib/common/common-utils';
import { NetworkStatusService } from '../../network-status-service/network-status.service';
import {
    GdprConsentNone,
    GfcEventsDefaultCommonData,
    Undefined,
    GfcHeadRequestStatusCodes,
    GfcAttachmentRequestStatusCodes
} from '../models/common.constants';
import {
    UserConsentInfo,
    ConsentLevel,
    GfcEventsCommonData,
    LogChunk,
    LogChunkParams,
    FeedbackDef,
    UserConsent,
    EventParams
} from '../models/common.interfaces';
import { LogQueueService } from './log-queue.service';

const MaxChunkFailureCount = 2;
// Current log collection timeout is 10 mins, so after 3 such consecutive log collections or 30mins, we
// close the current feedback session and create a new one when the buffer is full again
const MaxIdleTimeoutCounter = 3;

@Injectable({
    providedIn: 'root'
})
export class RemoteLoggingService implements OnDestroy {
    private defaultConsent = GdprConsentNone;
    private currentUserConsent = GdprConsentNone;
    private isUserConsentAvailable = false;
    private currentUserId = Undefined;
    private externalUserId = Undefined;
    private idpId = Undefined;
    private commonFeedbackData = GfcEventsDefaultCommonData;
    private eventData: FeedbackDef;
    private isGfcHeadRequestSent = false;
    private gfcService: GfcEventsEndpointsService;
    private isOnline = false;
    private logChunkReceivedSubscription: Subscription;
    private chunkFailureCount = 0;
    private attachmentUploadedPermitted = true;
    private streamSubSessionIds = [];
    private feedbackIdSubject = new ReplaySubject<string>(1);

    constructor(
        private injector: Injector,
        private logQueueService: LogQueueService,
        private networkStatusService: NetworkStatusService
    ) {}

    initialize() {
        this.logQueueService.initialize();
        this.logQueueService.addLogger('RemoteLoggingService', ['initialize']);
        this.networkStatusService.onlineStatusChanged.subscribe(this.onNetworkStatusChanged.bind(this));
        this.handleBrowserTabClosure();
        this.getLatestLogsChunk();
        this.trackIdleFeedbackSessions();
    }

    public set defaultConsentLevel(consentLevel: Readonly<UserConsent>) {
        this.defaultConsent = consentLevel;
    }

    public saveLogsToSessionStorage() {
        this.logQueueService.saveLogsToSessionStorage();
    }

    public restoreSavedLogs() {
        return this.logQueueService.readConsoleLogsFromSessionStorage();
    }

    /**
     * This method is used to close the current feedback session when there has been no
     * activity for a specified time. ie., close idle feedback session.
     * It is also called when we want to close the feedback session on game exit.
     */
    stopFeedbackUpload() {
        // In cases of GFC errors such as 466/462 where attachments are rejected OR the
        // max chunk failure count is reached, we should not attempt to close the feedback
        // session (by sending the last chunk below) either because the call is likely to fail.
        // Such sessions can be assumed to get expired.
        if (this.isChunkUploadPermitted()) {
            this.logQueueService.addLogger('RemoteLoggingService', ['Close current feedback session.']);
            this.logQueueService.stopChunkCollection(true);
            // Once you close the current feedback upload session, reset the flag below so that a new GFC
            // HEAD request can be created afresh the next time
            this.logQueueService.setHeadRequestStatus((this.isGfcHeadRequestSent = false));
            this.logQueueService.updateFeedbackSessionStatus(true);

            this.streamSubSessionIds.splice(0, this.streamSubSessionIds.length);
            const resetStreamerParams = {
                parameters: {
                    streamSessionId: Undefined,
                    streamSubSessionIds: this.streamSubSessionIds.filter(item => item !== '' || item !== null)
                }
            };

            this.eventData = this.eventData ?? ({} as FeedbackDef);
            this.eventData['parameters'] = { ...this.eventData?.parameters, ...resetStreamerParams.parameters };

            this.commonFeedbackData = { ...this.commonFeedbackData, ...this.eventData };
            this.logQueueService.updateEventData(this.eventData);
        }
    }

    /**
     * This method listens to the number of times chunk clearance has happened because of a timeout.
     * If the count reaches the max value, then stop further feedback upload.
     * If there is renewed log activity post closing of the previous feedback, then we initiate a new feedback session.
     */
    private trackIdleFeedbackSessions() {
        this.logQueueService
            .getRefreshFeedbackSubjectObservable()
            .pipe(
                map((idleTimeout: number) => {
                    if (idleTimeout === MaxIdleTimeoutCounter) {
                        this.logQueueService.addLogger('RemoteLoggingService', [
                            'Stopping feedback upload since max idle timeout duration is reached'
                        ]);
                        this.stopFeedbackUpload();
                    } else if (idleTimeout === 0) {
                        this.logQueueService.addLogger('RemoteLoggingService', ['Initiating a new feedback session..']);
                        this.sendNewGfcHeadRequest();
                    }
                })
            )
            .subscribe();
    }

    /**
     * Handle Offline states
     */
    private onNetworkStatusChanged(isOnline: boolean) {
        this.isOnline = isOnline;
    }

    private addGdprConsentFields() {
        const additionalCommonGdprInfo = {
            gdprFuncOptIn: this.currentUserConsent.functional ?? this.defaultConsent.functional,
            gdprTechOptIn: this.currentUserConsent.technical,
            gdprBehOptIn: this.currentUserConsent.behavioral,
            deviceGdprFuncOptIn: this.defaultConsent.functional,
            deviceGdprTechOptIn: this.defaultConsent.technical,
            deviceGdprBehOptIn: this.defaultConsent.behavioral
        };
        this.commonFeedbackData = extend(additionalCommonGdprInfo, this.commonFeedbackData);
    }

    private isChunkUploadPermitted(): boolean {
        return this.attachmentUploadedPermitted ? this.chunkFailureCount <= MaxChunkFailureCount : false;
    }

    private getLatestLogsChunk() {
        this.logChunkReceivedSubscription = this.logQueueService
            .onLogsChunkUpdated()
            .pipe(
                mergeMap((logChunk: LogChunk) => {
                    this.logQueueService.addLogger('RemoteLoggingService', [
                        'New batch! Attempting to send a batch of size: ',
                        logChunk.logString.length
                    ]);

                    if (!this.isChunkUploadPermitted()) {
                        // Not sending chunks due to multiple chunk send failures
                        this.logQueueService.addLogger('RemoteLoggingService', [
                            'Stopping log collection as max failure count is reached.'
                        ]);
                        this.logQueueService.stopChunkCollection();
                        return of(false);
                    } else if (this.isOnline && this.isUserConsentAvailable) {
                        return this.sendLogsChunk(logChunk.logString, logChunk.isLastChunk, logChunk.params);
                    } else {
                        this.logQueueService.chunkSendInProgress = false;
                        this.logQueueService.addLogger('RemoteLoggingService', [
                            'Unable to send batch of logs due to lack of user consent and/or user is offline. isUserConsentAvailable: ',
                            this.isUserConsentAvailable,
                            ', isOnline: ',
                            this.isOnline
                        ]);
                        return of(false);
                    }
                })
            )
            .subscribe(result => {
                if (!result) {
                    this.logQueueService.addLogger('RemoteLoggingService', ['Chunk send failure.']);
                }
            });
    }

    private terminateChunkReceivedSubscription() {
        if (this.logChunkReceivedSubscription) {
            this.logChunkReceivedSubscription.unsubscribe();
            this.logChunkReceivedSubscription = null;
        }
    }

    private sendNewGfcHeadRequest() {
        this.terminateChunkReceivedSubscription();
        // If the current feedback session has expired, then reset the flag below so that a new GFC
        // HEAD request can be created afresh
        this.logQueueService.setHeadRequestStatus((this.isGfcHeadRequestSent = false));
        // Generate new feedbackId because the previous one is in use or has completed/expired
        this.commonFeedbackData.feedbackId = generateUuid();
        this.feedbackIdSubject.next(this.commonFeedbackData.feedbackId);
        // Update clientTs as we are initiating a new feedback session
        this.commonFeedbackData.clientTs = new Date().toISOString();
        this.logQueueService.addLogger('RemoteLoggingService', [
            'New GFC feedback session created: ',
            this.commonFeedbackData.feedbackId
        ]);
        console.log('New GFC feedback session created: ', this.commonFeedbackData.feedbackId);
        this.sendGfcHeadRequest(this.commonFeedbackData);
    }

    private handleHeadRequestErrors(error: any) {
        switch (error.status) {
            case GfcHeadRequestStatusCodes.FeedbackIdInUse:
            case GfcHeadRequestStatusCodes.FeedbackIdExpired:
                this.logQueueService.setHeadRequestStatus((this.isGfcHeadRequestSent = false));
                this.sendNewGfcHeadRequest();
                break;
            case GfcHeadRequestStatusCodes.AttachmentsRejected:
                this.attachmentUploadedPermitted = false;
                this.logQueueService.stopChunkCollection();
                break;
        }
    }

    private sendGfcHeadRequest(eventData: any) {
        // Dynamic injection is required because LoggingService -> RemoteLoggingService ->
        // GfcEventsEndpointService -> AppConfig which in turn needs Logger.
        // Hence, delaying this injection to ensure appConfig is fully fetched before
        // we try to initialize GfcEventsEndpointsService
        this.gfcService = isUndefined(this.gfcService) ? this.injector.get(GfcEventsEndpointsService) : this.gfcService;

        // Send GFC Head request only when technical userConsent is available
        if (this.isOnline && this.isUserConsentAvailable) {
            eventData.sentTs = new Date().toISOString();
            this.gfcService.sendFeedbackEvent(eventData).subscribe(
                response => {
                    this.logQueueService.setHeadRequestStatus((this.isGfcHeadRequestSent = true));
                    if (isNil(this.logChunkReceivedSubscription)) {
                        this.getLatestLogsChunk();
                    }
                },
                error => {
                    this.logQueueService.addLogger('RemoteLoggingService', ['Send HEAD request error: ', error]);
                    this.handleHeadRequestErrors(error);
                }
            );
        }
    }

    /**
     * Before closing GFN application due to browser or tab closure, uploading all the logs is attempted. 'pageHide' event is used to notify such closure.
     * But it is not triggered on loading external URL in the same tab. If logout URL is opened in same tab, this function will be called explicitly to save all the logs
     * before loading the logout URL.
     */
    public handleExternalUrlLoad() {
        this.logQueueService.clearQueueAndBuffer(true);
    }

    private handleBrowserTabClosure() {
        // Using the `unload` event to determine terminate in page lifecycle it's unreliable, especially for iOS Safari.
        // Using `pagehide` event rather than `unload`, it will only be fired while closing tabs and refreshing pages.
        window.addEventListener('pagehide', e => {
            this.logQueueService.addLogger('RemoteLoggingService', ['Window pagehide triggered.']);
            // If the feedback session was closed after idle timeout and now just the tab is being closed
            // then we should ensure that a feedback session exists before trying to send the last chunk
            if (this.isGfcHeadRequestSent) {
                this.logQueueService.clearQueueAndBuffer(true);
            }
        });
    }

    private handleSpecialAttachmentErrors(error: any) {
        if (this.isChunkUploadPermitted() && error.status === GfcAttachmentRequestStatusCodes.InvalidFeedbackId) {
            this.sendNewGfcHeadRequest();
        } else if (
            // Updating the chunk offset so that the next attempt to send a chunk should happen with the correct offset
            this.isChunkUploadPermitted() &&
            error.status === GfcAttachmentRequestStatusCodes.NonSequentialChunk
        ) {
            const offsetHeader: number = error?.rawResponse?.headers?.get('x-data-offset');
            this.gfcService.updateOffsetOnSuccessfulAttachmentRequest(offsetHeader);
        } else if (!this.isChunkUploadPermitted()) {
            this.logQueueService.addLogger('RemoteLoggingService', [
                'Attachment error, stopping log collection as max failure count is reached.'
            ]);
            this.logQueueService.stopChunkCollection();
        }
    }

    private sendLogsChunk(logsString: string, isLastChunk: boolean, params?: LogChunkParams): Observable<boolean> {
        return this.gfcService.sendFeedbackChunkEvent(logsString, this.eventData, isLastChunk, params).pipe(
            mergeMap((response: object) => {
                this.gfcService.updateOffsetOnSuccessfulAttachmentRequest();
                this.logQueueService.onSendChunkSuccess();
                return of(true);
            }),
            catchError(error => {
                // If the feedback has expired, then create a new feedback request
                // and send chunks for a new feedbackId.
                this.logQueueService.addLogger('RemoteLoggingService', ['Failure in sending a chunk: ', error]);
                if (error?.status) {
                    // Only valid GFC errors should be accounted for stopping of log collection
                    this.chunkFailureCount++;
                    this.handleSpecialAttachmentErrors(error);
                    this.logQueueService.onSendChunkFailure(error);
                } else {
                    this.logQueueService.addLogger('RemoteLoggingService', [
                        'Non GFC error occurred: ',
                        error,
                        error?.message
                    ]);
                }
                return of(false);
            }),
            finalize(() => {
                if (isLastChunk && params?.isBrowserClosing) {
                    // Terminating the subscription only after the current bunch of logs is sent
                    // Don't terminate the session in browser not closed scenarios
                    this.terminateChunkReceivedSubscription();
                }
            })
        );
    }

    prepareGfcFeedbackEventParams(eventParameters: object) {
        this.eventData = { parameters: eventParameters };
        this.logQueueService.updateEventData(this.eventData);
    }

    prepareFeedbackCommonEventData(feedbackData: GfcEventsCommonData) {
        this.commonFeedbackData = merge(this.commonFeedbackData, feedbackData);
        this.feedbackIdSubject.next(this.commonFeedbackData.feedbackId);
        if (this.commonFeedbackData.userId === Undefined) {
            this.logQueueService.updateUserConsent((this.isUserConsentAvailable = false));
        }
    }

    /*
     * This is called by the client when the user logs in
     */
    setUserConsent(consentInfo: UserConsentInfo) {
        this.currentUserConsent = consentInfo.userConsent;
        if (isEmpty(consentInfo.externalUserId)) {
            // jarvis login
            this.externalUserId = consentInfo.userId;
            this.currentUserId = UndefinedString;
            this.idpId = UndefinedString;
        } else {
            this.currentUserId = consentInfo.userId;
            this.externalUserId = consentInfo.externalUserId;
            this.idpId = consentInfo.idpId;
        }

        if (this.currentUserConsent.technical === ConsentLevel.Full) {
            this.logQueueService.updateUserConsent((this.isUserConsentAvailable = true));
            this.addGdprConsentFields();
            this.commonFeedbackData.userId = this.currentUserId;
            this.commonFeedbackData.externalUserId = this.externalUserId;
            this.commonFeedbackData.idpId = this.idpId;
            this.commonFeedbackData.clientTs = new Date().toISOString();
            this.commonFeedbackData = extend(this.commonFeedbackData, this.eventData);
        } else {
            this.logQueueService.updateUserConsent((this.isUserConsentAvailable = false));
        }

        // send gfc feedback head request only once
        if (!this.isGfcHeadRequestSent) {
            this.sendGfcHeadRequest(this.commonFeedbackData);
        }
    }

    /*
     * This should be called by crimson/loggingService
     */
    collectLogString(logString: string) {
        this.logQueueService.appendLogEntry(logString);
    }

    setBufferConfiguration(config) {
        this.logQueueService.setBufferConfig(config);
    }

    updateEventDataParamIds(eventParams: EventParams) {
        // update event data object and emit
        if (eventParams.streamSessionId) {
            if (!this.streamSubSessionIds.includes(eventParams.streamSubSessionId)) {
                this.streamSubSessionIds.push(eventParams.streamSubSessionId);
            }
            if (this.streamSubSessionIds.filter(item => item === '').length !== 0) {
                this.streamSubSessionIds.splice(0, 1);
            }
            this.eventData = merge(this.eventData, {
                parameters: {
                    streamSessionId: eventParams.streamSessionId,
                    streamSubSessionIds: this.streamSubSessionIds
                }
            });
        } else if (eventParams.surveySessionId) {
            this.eventData = merge(this.eventData, {
                parameters: {
                    surveySessionId: eventParams.surveySessionId
                }
            });
        }
        this.logQueueService.updateEventData(this.eventData);
    }

    get feedbackId$(): Observable<string> {
        return this.feedbackIdSubject.asObservable();
    }

    ngOnDestroy() {
        this.terminateChunkReceivedSubscription();
    }
}
